import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import { homepageApi, type HomepageContentBundle } from '../services/homepageApi';
import ApplicationModal from '../components/ApplicationModal';
import { useApplication } from '../hooks/useApplication';
import { experienceLevelLabel } from '../utils/experienceLevel';
import VideoPlayer from '../components/VideoPlayer';
import './NewHomePage.css';

const NewHomePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { company } = useParams<{ company: string }>();
  const [selectedDomainForFamilies, setSelectedDomainForFamilies] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [testimonialImageErrors, setTestimonialImageErrors] = useState<Record<string, boolean>>({});
  const hasAutoScrolledRef = useRef(false);
  const isUserInteractionRef = useRef(false);
  const { isApplicationModalOpen, openApplicationModal, closeApplicationModal } = useApplication();
  
  // Single state for all homepage content
  const [homepageData, setHomepageData] = useState<HomepageContentBundle | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load data from API with fallback to static data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Add timeout to API call
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Zeitüberschreitung beim Laden der Inhalte.')), 10000) // 10 second timeout
        );
        
        const apiPromise = homepageApi.getCompleteHomepageContent();
        
        const content = await Promise.race([apiPromise, timeoutPromise]) as HomepageContentBundle;
        setHomepageData(content);     
      } catch (err) {
        // Fallback to static data with dynamic import
        console.error('Startseite konnte nicht geladen werden, statische Inhalte werden verwendet:', err);
        setError('Die aktuellen Inhalte sind vorübergehend nicht verfügbar.');
        const {
          ourPromise,
          testimonials,
          candidateExperience,
          whatWeOffer,
          companyValues,
          domains,
          families,
          jobs
        } = await import('../data/homepageData');
        
        const fallbackData: HomepageContentBundle = {
          heroSection: {
            title: "Gestalte die digitale Zukunft mit uns",
            subtitle: "Werde Teil der Digital Workforce Group und arbeite an spannenden IT- und Agile-Projekten für führende Banken und Fintech-Unternehmen",
            primaryButtonText: "Jetzt bewerben",
            secondaryButtonText: "Mehr erfahren",
            stats: []
          },
          companyInfo: {
            title: "Über die Digital Workforce Group AG",
            subtitle: "Lerne unser Unternehmen und unsere Vision kennen",
            name: "Digital Workforce Group AG",
            videoUrl: "https://player.vimeo.com/video/951992669?h=7a5a29b8ca",
            textSummary: {
              blocks: [{
                title: "Herzlich Willkommen bei uns",
                content: "Willkommen im Bereich IT & Agile Consulting bei der Digital Workforce Group AG!"
              }]
            }
          },
          ourPromise,
          testimonials,
          candidateExperience,
          whatWeOffer,
          companyValues,
          finalCTA: {
            title: "Bereit für den nächsten Schritt?",
            subtitle: "Starte jetzt deine Bewerbung und werde Teil der Digital Workforce Group",
            button1Text: "Jetzt bewerben",
            button2Text: "Direkt anrufen"
          },
          domainsSection: {
            title: "Unsere Jobbereiche",
            subtitle: "Entdecke deine Karrieremöglichkeiten in der Digital Workforce Group",
            domains
          },
          families,
          jobsSection: {
            title: "Unsere offenen Positionen",
            subtitle: "Finde die perfekte Rolle für deine Karriere",
            videoUrl: "https://player.vimeo.com/video/951992669?h=7a5a29b8ca",
            jobs
          },
          generatedAt: new Date().toISOString()
        };
        
        setHomepageData(fallbackData);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Auto-scroll effect - wait for data to load before scrolling
  useEffect(() => {
    // Only scroll if data is loaded
    if (!homepageData) return;
    
    const urlDomain = searchParams.get('domain');
    const urlFamily = searchParams.get('family');
    
    // Auto-scroll if parameters are from URL (not user interaction) and we haven't scrolled yet
    if ((urlDomain || urlFamily) && !hasAutoScrolledRef.current && !isUserInteractionRef.current) {
      hasAutoScrolledRef.current = true;
      // Delay to ensure DOM is rendered with the loaded data
      setTimeout(() => {
        const jobsSection = document.getElementById('jobs-section');
        if (jobsSection) {
          jobsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [searchParams, homepageData]);

  // Show loading until data loaded
  if (isLoading || !homepageData) {
    return (
      <div className="new-homepage">
        <div className="container" style={{ padding: '4rem', textAlign: 'center' }}>
          <h2>Lade Inhalte...</h2>
          {error && (
            <p style={{ color: '#666', marginTop: '1rem', fontSize: '0.9rem' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Extract data exactly like static imports
  const { 
    // ourPromise, // Commented out - section is currently disabled
    testimonials, 
    candidateExperience, 
    whatWeOffer, 
    // companyValues, // Commented out - section is currently disabled
    // finalCTA, // Commented out - section is currently disabled
    domainsSection, 
    families, 
    jobsSection 
  } = homepageData;
  
  const domains = domainsSection?.domains || [];
  const jobs = jobsSection?.jobs || [];
  
  const selectedDomain = searchParams.get('domain');
  const selectedFamily = searchParams.get('family');

  const getFilteredJobs = () => {
    let filtered = jobs;
    if (selectedDomain) {
      filtered = filtered.filter(job => job.domainSlug === selectedDomain);
    }
    if (selectedFamily) {
      filtered = filtered.filter(job => job.familySlug === selectedFamily);
    }
    return filtered;
  };

  const filteredJobs = getFilteredJobs();
  const markTestimonialImageError = (id: string | number) => {
    setTestimonialImageErrors((previous) => ({ ...previous, [String(id)]: true }));
  };

  const hasTestimonialImageError = (id: string | number) => Boolean(testimonialImageErrors[String(id)]);

  const getTestimonialInitials = (name: string) => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      return '?';
    }

    return trimmedName
      .split(/\s+/)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  };
  
  // Pagination constants and logic
  const JOBS_PER_PAGE = 6;
  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
  const endIndex = startIndex + JOBS_PER_PAGE;
  const currentPageJobs = filteredJobs.slice(startIndex, endIndex);

  const handleDomainSelect = (domainSlug: string) => {
    if (selectedDomainForFamilies === domainSlug) {
      setSelectedDomainForFamilies(null);
    } else {
      setSelectedDomainForFamilies(domainSlug);
    }
  };

  const handleFamilySelect = (domainSlug: string, familySlug: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('domain', domainSlug);
    newParams.set('family', familySlug);
    setSearchParams(newParams);
    
    setTimeout(() => {
      const jobsSection = document.getElementById('jobs-section');
      if (jobsSection) {
        jobsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    
    setSelectedDomainForFamilies(null);
  };

  const getFamiliesForDomain = (domainSlug: string) => {
    return families.filter(family => family.domainSlug === domainSlug);
  };

  const handleFilterSelect = (domainSlug?: string, familySlug?: string) => {
    // Mark as user interaction to prevent scrolling
    isUserInteractionRef.current = true;
    
    const newParams = new URLSearchParams(searchParams);
    
    if (!domainSlug && !familySlug) {
      newParams.delete('domain');
      newParams.delete('family');
    } else {
      if (domainSlug) {
        newParams.set('domain', domainSlug);
        newParams.delete('family');
      }
      if (familySlug) {
        newParams.set('family', familySlug);
      }
    }
    
    setSearchParams(newParams);
    // Reset to first page when filter changes
    setCurrentPage(1);
    
    // Reset user interaction flag after a short delay
    setTimeout(() => {
      isUserInteractionRef.current = false;
    }, 100);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getJobCountForDomain = (domainSlug: string) => {
    return jobs.filter(job => job.domainSlug === domainSlug).length;
  };

  const getJobCountForFamily = (familySlug: string) => {
    return jobs.filter(job => job.familySlug === familySlug).length;
  };

  const scrollToWhatWeOfferSection = () => {
    const whatWeOfferSection = document.getElementById('what-we-offer-section');
    if (whatWeOfferSection) {
      whatWeOfferSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="new-homepage">
      {/* 1. Hero Section - Static Content */}
      <section className="hero-section">
        <div className="hero-background">
          <video
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={homepageData?.heroSection.videoUrl || "/hero-video-optimized.mp4"} type="video/mp4" />
            Ihr Browser unterstützt die Videowiedergabe nicht.
          </video>
          <div className="video-overlay"></div>
        </div>
        
        <div className="container hero-content">
          <h1 className="hero-title">{homepageData?.heroSection.title || "Gestalte die digitale Zukunft mit uns"}</h1>
          <p className="hero-subtitle">
            {homepageData?.heroSection.subtitle || "Werde Teil der Digital Workforce Group und arbeite an spannenden IT- und Agile-Projekten für führende Banken und Fintech-Unternehmen"}
          </p>
          
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={() => openApplicationModal()}>
              {homepageData?.heroSection.primaryButtonText || "Jetzt bewerben"}
            </button>
            <button className="btn btn-secondary" onClick={scrollToWhatWeOfferSection}>
              {homepageData?.heroSection.secondaryButtonText || "Mehr erfahren"}
            </button>
          </div>
        </div>
      </section>

      {/* 2. Company Video Section - Dynamic Content */}
      <section className="company-section">
        <div className="container">
          <h2 className="section-title">{homepageData?.companyInfo.title || "Über die Digital Workforce Group AG"}</h2>
          <p className="section-subtitle">{homepageData?.companyInfo.subtitle || "Lerne unser Unternehmen und unsere Vision kennen"}</p>
          
          <div className="company-video-section">
            <VideoPlayer
              videoUrl={homepageData?.companyInfo.videoUrl || "https://player.vimeo.com/video/951992669?h=7a5a29b8ca"}
              title="Unternehmensvideo"
            />
          </div>

          <div className="text-summary-section">
            <div className="text-blocks">
              {(homepageData?.companyInfo.textSummary.blocks || []).map((block, index) => (
                <div key={index} className="text-block">
                  <strong>{block.title}</strong>
                  <p>{block.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Jobs Section - Dynamic from Backend */}
      <section id="jobs-section" className="jobs-section-wrapper">
        <div className="container">
          <div className="jobs-section">
            <h2 className="section-title">{jobsSection?.title || "Unsere offenen Positionen"}</h2>
            <p className="section-subtitle">{jobsSection?.subtitle || "Finde die perfekte Rolle für deine Karriere"}</p>

            {/* Filter Pills - Dynamic from Backend */}
            <div id="job-filter-pills" className="job-filter-pills">
              <button
                className={`filter-pill ${!selectedDomain && !selectedFamily ? 'active' : ''}`}
                onClick={() => handleFilterSelect()}
              >
                Alle Positionen <span className="pill-count">{jobs.length}</span>
              </button>
              {domains.map(domain => (
                <button
                  key={domain.id}
                  className={`filter-pill ${selectedDomain === domain.slug ? 'active' : ''}`}
                  onClick={() => handleFilterSelect(domain.slug)}
                >
                  {domain.title} <span className="pill-count">{getJobCountForDomain(domain.slug)}</span>
                </button>
              ))}
            </div>

            {/* Jobs Grid - Dynamic from Backend */}
            <div id="jobs-grid" className="jobs-grid-notion">
              {currentPageJobs.map((job) => (
                <div key={job.id} className="job-card-notion">
                  <div className="job-card-top">
                    <h3 className="job-title">{job.title}</h3>

                    <div className="job-meta-info">
                      {job.experienceLevel && (
                        <p className="job-level">
                          {experienceLevelLabel(job.experienceLevel)}
                        </p>
                      )}
                      {job.location && <p className="job-location">{job.location}</p>}
                    </div>
                  </div>

                  <div className="job-card-middle">
                    <p className="job-description">{job.metaDescription}</p>

                    <div className="job-requirements">
                      {job.skills[0] && <div className="requirement-item">{job.skills[0]}</div>}
                      {job.skills[1] && <div className="requirement-item">{job.skills[1]}</div>}
                      {job.skills[2] && <div className="requirement-item">{job.skills[2]}</div>}
                    </div>
                  </div>

                  <div className="job-card-bottom">
                    {job.salary && <div className="job-salary-banner">{job.salary}</div>}
                    <Link
                      to={company ? `/${company}/job/${job.slug}` : `/job/${job.slug}`}
                      className="job-details-btn"
                    >
                      Details ansehen
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Seite {currentPage} von {totalPages} ({filteredJobs.length} Jobs gesamt)
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Vorherige
                  </button>

                  {/* Page Numbers */}
                  <div className="page-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                      <button
                        key={pageNum}
                        className={`page-number ${pageNum === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Nächste →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 4. Call to Action Section - Dynamic Content (shown twice) */}
      <section className="cta-section-wrapper">
        <div className="container">
          <div className="cta-section-blue">
            <h2 className="cta-title">{homepageData?.finalCTA.title || "Bereit für den nächsten Schritt?"}</h2>
            <p className="cta-subtitle">{homepageData?.finalCTA.subtitle || "Starte jetzt deine Bewerbung und werde Teil der Digital Workforce Group"}</p>

            <div className="cta-buttons">
              <button className="btn btn-white" onClick={() => openApplicationModal()}>
                {homepageData?.finalCTA.button1Text || "Jetzt bewerben"}
              </button>
              <button className="btn btn-outline-white" onClick={() => window.location.href = 'tel:+4969348687720'}>
                {homepageData?.finalCTA.button2Text || "Direkt anrufen"}
              </button>
            </div>
          </div>
        </div>
      </section>

      

      {/* 5. Domain Selection Section - Dynamic from Backend */}
      <section className="domain-selection-section">
        <div className="container">
          <h2 className="section-title">{domainsSection?.title || "Unsere Jobbereiche"}</h2>
          <p className="section-subtitle">{domainsSection?.subtitle || "Entdecke deine Karrieremöglichkeiten in der Digital Workforce Group"}</p>
          
          {/* Rich Domain Cards */}
          <div className={`domains-grid-rich ${selectedDomainForFamilies ? 'has-expanded' : ''}`}>
            {domains.map(domain => (
              <div
                key={domain.id}
                className={`domain-wrapper ${selectedDomainForFamilies === domain.slug ? 'expanded' : ''}`}
              >
                <div
                  className={`domain-card-rich ${selectedDomainForFamilies === domain.slug ? 'expanded' : ''} domain-${domain.color}`}
                  onClick={() => handleDomainSelect(domain.slug)}
                >
                  <div className="domain-header">
                    <div className="domain-title-section">
                      <h3 className="domain-title">{domain.title}</h3>
                      <div className="domain-meta">
                        <span className="job-count">{getJobCountForDomain(domain.slug)} Positionen</span>
                        <span className="salary-range">{domain.averageSalary}</span>
                      </div>
                    </div>
                    <div className="expand-indicator">
                      {selectedDomainForFamilies === domain.slug ? '✕' : '▼'}
                    </div>
                  </div>
                  
                  <p className="domain-description">{domain.description}</p>
                  
                  <div className="domain-details">
                    <div className="detail-section">
                      <h5>Karriereweg</h5>
                      <p>{domain.careerPath}</p>
                    </div>

                    <div className="detail-section">
                      <h5>Schlüsseltechnologien</h5>
                      <div className="tech-tags">
                        {domain.keyTechnologies.slice(0, 4).map((tech, index) => (
                          <span key={index} className="tech-tag">{tech}</span>
                        ))}
                        {domain.keyTechnologies.length > 4 && (
                          <span className="tech-tag more">+{domain.keyTechnologies.length - 4} weitere</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="domain-description-long">
                      <p>{domain.longDescription}</p>
                    </div>
                  </div>

                  {/* Family Selection - Now inline within the expanded domain card */}
                  {selectedDomainForFamilies === domain.slug && (
                    <div className="families-section">
                      <h4>Wähle eine Jobfamilie in {domain.title}</h4>
                      <div className="families-grid">
                        {getFamiliesForDomain(domain.slug).map(family => (
                          <div
                            key={family.id}
                            className="family-card"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFamilySelect(domain.slug, family.slug);
                            }}
                          >
                            <div className="family-header">
                              <div className="family-title-section">
                                <h5>{family.title}</h5>
                                <span className="family-job-count">{getJobCountForFamily(family.slug)} Jobs</span>
                              </div>
                            </div>
                            <p className="family-description">{family.description}</p>
                            <div className="family-skills">
                              {family.keySkills.map((skill, index) => (
                                <span key={index} className="skill-tag">{skill}</span>
                              ))}
                            </div>
                            <div className="family-action">
                              <span>Jobs anzeigen →</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="domain-action">
                    <span className="action-text">
                      {selectedDomainForFamilies === domain.slug ? 'Bereich ausblenden' : 'Bereich erkunden'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. What We Offer Section - Dynamic from Backend */}
      <section className="what-we-offer-section" id="what-we-offer-section">
        <div className="container">
          <h2 className="section-title">{whatWeOffer.title}</h2>
          <p className="section-subtitle">{whatWeOffer.subtitle}</p>
          
          {/* Benefits Grid with mixed varied layout throughout */}
          <div className="offer-benefits-mixed-grid">
            {whatWeOffer.benefits.map((benefit, index) => {
              // Create a more varied mixed pattern across the entire grid
              let cardClass = 'offer-card';
              
              // Mixed pattern for 6 cards: variety across all columns
              const patterns: Record<number, string> = {
                0: 'offer-card-primary offer-card-wide', // Wide blue (spans 2 cols)
                1: 'offer-card-secondary', // White (normal)
                2: 'offer-card-secondary', // White (normal)
                3: 'offer-card-primary', // Blue (normal)
                4: 'offer-card-secondary', // White (normal)
                5: 'offer-card-primary offer-card-wide' // Wide blue (spans 2 cols)
              };
              
              // Apply pattern based on index
              if (patterns[index] !== undefined) {
                cardClass += ' ' + patterns[index];
              } else {
                // Fallback for more items
                cardClass += index % 3 === 0 ? ' offer-card-primary' : ' offer-card-secondary';
              }
              
              return (
                <div key={benefit.id} className={cardClass}>
                  <h3 className="offer-card-title">{benefit.title}</h3>
                  <p className="offer-card-description">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. Testimonials Section - Dynamic from Backend */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">{homepageData?.testimonials.title || testimonials.title}</h2>
          <p className="section-subtitle">{homepageData?.testimonials.subtitle || testimonials.subtitle}</p>
          
          <div className="testimonials-grid">
            {(homepageData?.testimonials.testimonials || testimonials.testimonials).map(testimonial => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="testimonial-image">
                  {hasTestimonialImageError(testimonial.id) ? (
                    <div className="image-fallback">{getTestimonialInitials(testimonial.name)}</div>
                  ) : (
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      onError={() => markTestimonialImageError(testimonial.id)}
                    />
                  )}
                </div>
                <blockquote className="testimonial-quote">
                  "{testimonial.quote}"
                </blockquote>
                <div className="testimonial-attribution">
                  <div className="testimonial-name">{testimonial.name}</div>
                  <div className="testimonial-role">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Candidate Experience Section - Dynamic from Backend */}
      <section className="candidate-experience-section">
        <div className="container">
          <h2 className="section-title">{homepageData?.candidateExperience.title || candidateExperience.title}</h2>
          <p className="section-subtitle">{homepageData?.candidateExperience.subtitle || candidateExperience.subtitle}</p>
          
          {/* Candidate Experience Video */}
          <div className="candidate-video-section">
            <div className="video-embed-container">
              <VideoPlayer
                videoUrl={homepageData?.candidateExperience.videoUrl || candidateExperience.videoUrl}
                title="Video zum Bewerbungsablauf"
              />
              <div className="video-title-overlay">
                {homepageData?.candidateExperience.videoTitle || candidateExperience.videoTitle}
              </div>
            </div>
          </div>

          {/* Process Steps with Inline Description */}
          <div className="process-steps">
            {(homepageData?.candidateExperience.processSteps || candidateExperience.processSteps).map((step, index) => (
              <div key={step.id} className="process-step">
                <div className="step-number">{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

     {/* Duplicate CTA Banner */}
      <section className="cta-section-wrapper">
        <div className="container">
          <div className="cta-section-blue">
            <h2 className="cta-title">{homepageData?.finalCTA.title || "Bereit für den nächsten Schritt?"}</h2>
            <p className="cta-subtitle">{homepageData?.finalCTA.subtitle || "Starte jetzt deine Bewerbung und werde Teil der Digital Workforce Group"}</p>

            <div className="cta-buttons">
              <button className="btn btn-white" onClick={() => openApplicationModal()}>
                {homepageData?.finalCTA.button1Text || "Jetzt bewerben"}
              </button>
              <button className="btn btn-outline-white" onClick={() => window.location.href = 'tel:+4069348687720'}>
                {homepageData?.finalCTA.button2Text || "Direkt anrufen"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Our Promise Section - Dynamic from Backend */}
      {/* <section className="our-promise-section">
        <div className="container">
          <h2 className="section-title">{ourPromise.title}</h2>
          <p className="section-subtitle">Mehr als nur ein Job - werde Teil unserer Gemeinschaft</p>
          
          <div className="promise-benefits-grid">
            {ourPromise.benefits.map((benefit, index) => {
              // First card is large blue, next 4 are white, last is wide blue
              let cardClass = 'promise-card';
              if (index === 0) {
                cardClass += ' promise-card-primary promise-card-large';
              } else if (index === ourPromise.benefits.length - 1 && index > 4) {
                cardClass += ' promise-card-primary promise-card-wide';
              } else {
                cardClass += ' promise-card-secondary';
              }
              
              return (
                <div key={benefit.id} className={cardClass}>
                  <div className="promise-card-icon">{benefit.icon}</div>
                  <h3 className="promise-card-title">{benefit.title}</h3>
                  <p className="promise-card-description">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* 10. Company Values Section - Dynamic from Backend */}
      {/* <section className="company-values-section">
        <div className="container">
          <h2 className="section-title">{companyValues.title}</h2>
          <p className="section-subtitle">{companyValues.subtitle}</p>
          
          <div className="text-summary-section">
            <div className="text-blocks">
              <div className="text-block">
                <strong>Unser Arbeitsleben</strong>
                <p>{companyValues.description}</p>
              </div>
            </div>
          </div>
          
          <div className="company-values-grid">
            {companyValues.values.map(value => (
              <div
                key={value.id}
                className={`company-value-card ${value.type === 'primary' ? 'company-value-primary' : 'company-value-secondary'}`}
              >
                <div className="company-value-icon">
                  {value.icon}
                </div>
                <h3 className="company-value-title">{value.title}</h3>
                <p className="company-value-description">{value.description}</p>
              </div>
            ))}
          </div>
          
          <div className="company-statistics">
            {companyValues.statistics.map(stat => (
              <div key={stat.id} className="company-statistic-card">
                <div className="company-stat-icon">
                  {stat.icon}
                </div>
                <div className="company-stat-content">
                  {stat.title && <h3 className="company-stat-title">{stat.title}</h3>}
                  {stat.subtitle && <h4 className="company-stat-subtitle">{stat.subtitle}</h4>}
                  <p className="company-stat-description">{stat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* 10. Our Promise Section - Dynamic from Backend - Mixed Layout like Company Values */}
      {/* <section className="our-promise-section">
        <div className="container">
          <h2 className="section-title">{ourPromise.title}</h2>
          
          <div className="promise-video-section">
            <div className="video-embed-container">
              <iframe
                title="Video zu unserem Versprechen"
                src={ourPromise.videoUrl}
                frameBorder="0"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
              ></iframe>
              <div className="video-title-overlay">
                {ourPromise.videoTitle}
              </div>
            </div>
          </div>
          
          <div className="promise-mixed-layout">
            {ourPromise.benefits.map((benefit, index) => {
              // Create variable card types based on index for visual interest
              const cardTypes = ['wide-blue', 'small-white', 'medium-white', 'full-blue', 'small-white'];
              const cardType = cardTypes[index % cardTypes.length];
              
              return (
                <div
                  key={benefit.id}
                  className={`promise-card promise-card-${cardType}`}
                >
                  <div className="promise-card-icon">
                    {benefit.icon}
                  </div>
                  <div className="promise-card-content">
                    <h3 className="promise-card-title">{benefit.title}</h3>
                    <p className="promise-card-description">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* 11. Final Call-to-Action Section - Dynamic from Backend - COMMENTED OUT (Duplicate CTA) */}
      {/*
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-content">
            <h2 className="final-cta-title">{finalCTA.title}</h2>
            <p className="final-cta-subtitle">{finalCTA.subtitle}</p>
            <Link to={finalCTA.buttonUrl} className="final-cta-button">
              {finalCTA.buttonText}
            </Link>
          </div>
        </div>
      </section>
      */}
      
      {/* Application Modal */}
      <ApplicationModal 
        isOpen={isApplicationModalOpen}
        onClose={() => closeApplicationModal()}
      />
    </div>
  );
};

export default NewHomePage;
